import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Typography, Paper, Tabs, Tab, Button, TextField, MenuItem, Select,
  FormControl, InputLabel, IconButton, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Alert, Snackbar, CircularProgress,
  Grid, Card, CardContent, Divider, Stack, Switch, FormControlLabel,
  Autocomplete,
} from '@mui/material';
import { MdAdd, MdEdit, MdDelete, MdClose } from 'react-icons/md';

const API = 'https://api.bharatdarshan.app/api/rojgar';
const defaultCat = { name: '', nameHindi: '', iconEmoji: '', isActive: true };
const defaultPlan = (planType) => ({ name: '', planType, price: 0, duration: 30, features: [], isActive: true, isPopular: false, order: 0 });

const SEEKER_FEATURE_OPTIONS = [
  '5 job applications/month', '10 job applications/month', '20 job applications/month',
  '50 job applications/month', 'Unlimited job applications', 'Resume builder',
  'Priority listing in candidate search', 'Chat support with employers', 'Apply with one click',
  'Job alerts via SMS', 'Job alerts via WhatsApp', 'Featured profile badge',
  'View employer contact details', 'Premium customer support', 'Application status tracking',
];

const PROVIDER_FEATURE_OPTIONS = [
  '5 active job posts', '10 active job posts', '25 active job posts',
  '50 active job posts', 'Unlimited job posts', 'Featured job listing',
  'Highlighted listing (top of list)', 'View candidate contact details',
  'Direct chat with candidates', 'Bulk candidate messaging', 'Resume database access',
  'Priority customer support', 'Job posting analytics', 'Auto-renewal of expired posts',
  'Verified employer badge',
];

function PlanForm({ form, setForm, editId, planType, onSave, onCancel }) {
  const [customFeat, setCustomFeat] = useState('');
  const featureOptions = planType === 'seeker' ? SEEKER_FEATURE_OPTIONS : PROVIDER_FEATURE_OPTIONS;
  const features = Array.isArray(form.features) ? form.features : [];
  const accent = planType === 'seeker' ? '#1976d2' : '#2e7d32';

  const addFeature = (val) => {
    const v = (val || '').trim();
    if (!v) return;
    setForm(p => {
      const cur = Array.isArray(p.features) ? p.features : [];
      if (cur.includes(v)) return p;
      return { ...p, features: [...cur, v] };
    });
  };
  const removeFeature = (idx) => {
    setForm(p => ({ ...p, features: (p.features || []).filter((_, i) => i !== idx) }));
  };

  return (
    <Paper sx={{ p: 3, mb: 3, borderLeft: `4px solid ${accent}` }} elevation={2}>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <Typography variant="h6" sx={{ color: accent, fontWeight: 700 }}>
          {planType === 'seeker' ? '🔍' : '🏢'} {editId ? 'Edit' : 'Add New'} {planType === 'seeker' ? 'Job Seeker' : 'Job Provider'} Plan
        </Typography>
        <Chip label={planType.toUpperCase()} size="small" sx={{ ml: 'auto', bgcolor: accent, color: '#fff' }} />
      </Stack>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField label="Plan Name *" fullWidth size="small" value={form.name}
            onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Premium" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField label="Price (₹)" type="number" fullWidth size="small" value={form.price}
            onChange={(e) => setForm(p => ({ ...p, price: Number(e.target.value) }))} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField label="Duration (days)" type="number" fullWidth size="small" value={form.duration}
            onChange={(e) => setForm(p => ({ ...p, duration: Number(e.target.value) }))} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={form.isActive ? 'true' : 'false'}
              onChange={(e) => setForm(p => ({ ...p, isActive: e.target.value === 'true' }))}>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField label="Sort Order" type="number" fullWidth size="small" value={form.order}
            onChange={(e) => setForm(p => ({ ...p, order: Number(e.target.value) }))} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControlLabel control={<Switch checked={!!form.isPopular}
            onChange={(e) => setForm(p => ({ ...p, isPopular: e.target.checked }))} />}
            label="Popular" />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#555' }}>Features</Typography>
          <Stack direction="row" spacing={1} mb={1.5}>
            <Autocomplete freeSolo size="small" options={featureOptions} sx={{ flex: 1 }}
              onChange={(_, val) => addFeature(val)}
              renderInput={(p) => <TextField {...p} placeholder="Select or type feature" />} />
            <TextField size="small" placeholder="Custom feature" value={customFeat}
              onChange={(e) => setCustomFeat(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { addFeature(customFeat); setCustomFeat(''); } }} />
            <Button variant="outlined" onClick={() => { addFeature(customFeat); setCustomFeat(''); }}>Add</Button>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            {features.length === 0 ? (
              <Typography variant="caption" sx={{ color: '#999' }}>No features added</Typography>
            ) : features.map((f, i) => (
              <Chip key={i} label={f} onDelete={() => removeFeature(i)} sx={{ bgcolor: '#fff3e0' }} />
            ))}
          </Stack>
        </Grid>
      </Grid>
      <Stack direction="row" spacing={1} mt={3}>
        <Button variant="contained" onClick={() => onSave(form, editId, planType)}
          sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e65a00' } }}>
          {editId ? 'Update Plan' : 'Save Plan'}
        </Button>
        <Button variant="outlined" onClick={onCancel}>Cancel</Button>
      </Stack>
    </Paper>
  );
}

function PlanTable({ plans, onEdit, onDelete }) {
  if (!plans || plans.length === 0) {
    return <Paper sx={{ p: 4, textAlign: 'center', color: '#999' }}>No plans yet.</Paper>;
  }
  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead sx={{ bgcolor: '#f5f5f5' }}>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Price</TableCell>
            <TableCell>Duration</TableCell>
            <TableCell>Features</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Popular</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {plans.map((p, i) => (
            <TableRow key={p._id} hover>
              <TableCell>{i + 1}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{p.name}</TableCell>
              <TableCell>₹{p.price}</TableCell>
              <TableCell>{p.duration} days</TableCell>
              <TableCell>{Array.isArray(p.features) ? p.features.length : 0}</TableCell>
              <TableCell>
                <Chip label={p.isActive !== false ? 'Active' : 'Inactive'} size="small"
                  color={p.isActive !== false ? 'success' : 'default'} />
              </TableCell>
              <TableCell>{p.isPopular ? '⭐' : '—'}</TableCell>
              <TableCell align="right">
                <IconButton size="small" onClick={() => onEdit(p)}><MdEdit color="#1976d2" /></IconButton>
                <IconButton size="small" onClick={() => onDelete(p._id)}><MdDelete color="#d32f2f" /></IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function RojgarManagement() {
  const [activeTab, setActiveTab] = useState(0);
  const [msg, setMsg] = useState({ open: false, text: '', type: 'success' });

  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(false);
  const [catForm, setCatForm] = useState(defaultCat);
  const [editCatId, setEditCatId] = useState(null);

  const [allPlans, setAllPlans] = useState([]);
  const [planLoading, setPlanLoading] = useState(false);
  const [seekerForm, setSeekerForm] = useState(defaultPlan('seeker'));
  const [providerForm, setProviderForm] = useState(defaultPlan('provider'));
  const [editSeekerId, setEditSeekerId] = useState(null);
  const [editProviderId, setEditProviderId] = useState(null);
  const [showSeekerForm, setShowSeekerForm] = useState(false);
  const [showProviderForm, setShowProviderForm] = useState(false);

  const showMsg = (text, type = 'success') => setMsg({ open: true, text, type });

  const loadCategories = async () => {
    setCatLoading(true);
    try {
      const r = await axios.get(`${API}/admin/categories`);
      setCategories(r.data?.data || r.data || []);
    } catch (e) { showMsg('Failed to load categories', 'error'); }
    setCatLoading(false);
  };

  const loadPlans = async () => {
    setPlanLoading(true);
    try {
      const r = await axios.get(`${API}/admin/plans`);
      setAllPlans(r.data?.data || r.data || []);
    } catch (e) { showMsg('Failed to load plans', 'error'); }
    setPlanLoading(false);
  };

  useEffect(() => { loadCategories(); loadPlans(); }, []);

  const seekerPlans = allPlans.filter(p => p.planType === 'seeker');
  const providerPlans = allPlans.filter(p => p.planType === 'provider');

  const saveCat = async () => {
    if (!catForm.name.trim()) return showMsg('Category name is required', 'error');
    try {
      if (editCatId) {
        await axios.put(`${API}/admin/category/${editCatId}`, catForm);
        showMsg('Category updated');
      } else {
        await axios.post(`${API}/admin/category`, catForm);
        showMsg('Category created');
      }
      setCatForm(defaultCat); setEditCatId(null); loadCategories();
    } catch (e) { showMsg(e?.response?.data?.message || 'Error saving category', 'error'); }
  };

  const deleteCat = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try { await axios.delete(`${API}/admin/category/${id}`); showMsg('Category deleted'); loadCategories(); }
    catch { showMsg('Error deleting', 'error'); }
  };

  const editCat = (cat) => {
    setEditCatId(cat._id);
    setCatForm({ name: cat.name || '', nameHindi: cat.nameHindi || '', iconEmoji: cat.iconEmoji || '', isActive: cat.isActive !== false });
    window.scrollTo(0, 0);
  };

  const savePlan = async (form, editId, planType) => {
    if (!form.name.trim()) return showMsg('Plan name is required', 'error');
    const featuresArr = Array.isArray(form.features)
      ? form.features.filter(Boolean)
      : (typeof form.features === 'string' ? form.features.split(',').map(f => f.trim()).filter(Boolean) : []);
    const payload = { ...form, planType, features: featuresArr };
    try {
      if (editId) {
        await axios.put(`${API}/admin/plans/${editId}`, payload);
        showMsg(`${planType === 'seeker' ? 'Seeker' : 'Provider'} plan updated`);
      } else {
        await axios.post(`${API}/admin/plans`, payload);
        showMsg(`${planType === 'seeker' ? 'Seeker' : 'Provider'} plan created`);
      }
      if (planType === 'seeker') { setSeekerForm(defaultPlan('seeker')); setEditSeekerId(null); setShowSeekerForm(false); }
      else { setProviderForm(defaultPlan('provider')); setEditProviderId(null); setShowProviderForm(false); }
      loadPlans();
    } catch (e) { showMsg(e?.response?.data?.message || 'Error saving plan', 'error'); }
  };

  const deletePlan = async (id) => {
    if (!window.confirm('Delete this plan?')) return;
    try { await axios.delete(`${API}/admin/plans/${id}`); showMsg('Plan deleted'); loadPlans(); }
    catch { showMsg('Error deleting', 'error'); }
  };

  const editSeekerPlan = (plan) => {
    setEditSeekerId(plan._id);
    setSeekerForm({
      name: plan.name || '', planType: 'seeker',
      price: plan.price || 0, duration: plan.duration || 30,
      features: Array.isArray(plan.features) ? plan.features : (plan.features ? String(plan.features).split(',').map(f => f.trim()).filter(Boolean) : []),
      isActive: plan.isActive !== false, isPopular: plan.isPopular || false, order: plan.order || 0,
    });
    setShowSeekerForm(true);
    window.scrollTo(0, 0);
  };

  const editProviderPlan = (plan) => {
    setEditProviderId(plan._id);
    setProviderForm({
      name: plan.name || '', planType: 'provider',
      price: plan.price || 0, duration: plan.duration || 30,
      features: Array.isArray(plan.features) ? plan.features : (plan.features ? String(plan.features).split(',').map(f => f.trim()).filter(Boolean) : []),
      isActive: plan.isActive !== false, isPopular: plan.isPopular || false, order: plan.order || 0,
    });
    setShowProviderForm(true);
    window.scrollTo(0, 0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#333' }}>Rojgar Management</Typography>
        <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
          Manage job categories and subscription plans for seekers and providers
        </Typography>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
          textColor="primary" indicatorColor="primary"
          sx={{ '& .MuiTab-root': { fontWeight: 600, textTransform: 'none' } }}>
          <Tab label={`Job Categories (${categories.length})`} />
          <Tab label={`Seeker Plans (${seekerPlans.length})`} />
          <Tab label={`Provider Plans (${providerPlans.length})`} />
        </Tabs>
      </Paper>

      {/* Categories Tab */}
      {activeTab === 0 && (
        <Box>
          <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#333', mb: 0.5 }}>
              {editCatId ? 'Edit Category' : 'Add New Category'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#888', mb: 2, display: 'block' }}>
              These appear in the app for job posting and filtering
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Category Name *" fullWidth size="small"
                  placeholder="e.g. IT & Software" value={catForm.name}
                  onChange={(e) => setCatForm(p => ({ ...p, name: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Hindi Name" fullWidth size="small"
                  placeholder="e.g. आईटी और सॉफ्टवेयर" value={catForm.nameHindi}
                  onChange={(e) => setCatForm(p => ({ ...p, nameHindi: e.target.value }))} />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <TextField label="Emoji Icon" fullWidth size="small"
                  placeholder="💻" value={catForm.iconEmoji}
                  onChange={(e) => setCatForm(p => ({ ...p, iconEmoji: e.target.value }))} />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select label="Status" value={catForm.isActive ? 'true' : 'false'}
                    onChange={(e) => setCatForm(p => ({ ...p, isActive: e.target.value === 'true' }))}>
                    <MenuItem value="true">Active</MenuItem>
                    <MenuItem value="false">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Stack direction="row" spacing={1} mt={2}>
              <Button variant="contained" startIcon={<MdAdd />} onClick={saveCat}
                sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e65a00' } }}>
                {editCatId ? 'Update Category' : 'Add Category'}
              </Button>
              {editCatId && (
                <Button variant="outlined" onClick={() => { setEditCatId(null); setCatForm(defaultCat); }}>
                  Cancel
                </Button>
              )}
            </Stack>
          </Paper>

          <Paper>
            <Box sx={{ px: 2, py: 1.5, bgcolor: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                All Categories ({categories.length})
              </Typography>
            </Box>
            {catLoading ? (
              <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Hindi</TableCell>
                      <TableCell>Icon</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {categories.length === 0 ? (
                      <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: '#999' }}>No categories yet.</TableCell></TableRow>
                    ) : categories.map((cat, i) => (
                      <TableRow key={cat._id} hover>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{cat.name}</TableCell>
                        <TableCell sx={{ color: '#666' }}>{cat.nameHindi || '—'}</TableCell>
                        <TableCell sx={{ fontSize: 22 }}>{cat.iconEmoji || '—'}</TableCell>
                        <TableCell>
                          <Chip label={cat.isActive !== false ? 'Active' : 'Inactive'} size="small"
                            color={cat.isActive !== false ? 'success' : 'default'} />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => editCat(cat)}><MdEdit color="#1976d2" /></IconButton>
                          <IconButton size="small" onClick={() => deleteCat(cat._id)}><MdDelete color="#d32f2f" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Box>
      )}

      {/* Seeker Plans Tab */}
      {activeTab === 1 && (
        <Box>
          <Alert severity="info" icon={<span style={{ fontSize: 22 }}>🔍</span>} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Job Seeker Plans</Typography>
            <Typography variant="caption">Plans for users who search and apply for jobs. Controls monthly application limits and features.</Typography>
          </Alert>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#555' }}>
              {seekerPlans.length} plan{seekerPlans.length !== 1 ? 's' : ''}
            </Typography>
            {!showSeekerForm && !editSeekerId && (
              <Button variant="contained" startIcon={<MdAdd />} onClick={() => setShowSeekerForm(true)}
                sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#115293' } }}>
                Add Seeker Plan
              </Button>
            )}
          </Stack>
          {(showSeekerForm || editSeekerId) && (
            <PlanForm form={seekerForm} setForm={setSeekerForm} editId={editSeekerId} planType="seeker"
              onSave={savePlan}
              onCancel={() => { setShowSeekerForm(false); setEditSeekerId(null); setSeekerForm(defaultPlan('seeker')); }} />
          )}
          {planLoading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
            : <PlanTable plans={seekerPlans} onEdit={editSeekerPlan} onDelete={deletePlan} />}
        </Box>
      )}

      {/* Provider Plans Tab */}
      {activeTab === 2 && (
        <Box>
          <Alert severity="success" icon={<span style={{ fontSize: 22 }}>🏢</span>} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Job Provider Plans</Typography>
            <Typography variant="caption">Plans for employers who post job listings. Controls active job posts and hiring features.</Typography>
          </Alert>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#555' }}>
              {providerPlans.length} plan{providerPlans.length !== 1 ? 's' : ''}
            </Typography>
            {!showProviderForm && !editProviderId && (
              <Button variant="contained" startIcon={<MdAdd />} onClick={() => setShowProviderForm(true)}
                sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}>
                Add Provider Plan
              </Button>
            )}
          </Stack>
          {(showProviderForm || editProviderId) && (
            <PlanForm form={providerForm} setForm={setProviderForm} editId={editProviderId} planType="provider"
              onSave={savePlan}
              onCancel={() => { setShowProviderForm(false); setEditProviderId(null); setProviderForm(defaultPlan('provider')); }} />
          )}
          {planLoading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
            : <PlanTable plans={providerPlans} onEdit={editProviderPlan} onDelete={deletePlan} />}
        </Box>
      )}

      <Snackbar open={msg.open} autoHideDuration={4000} onClose={() => setMsg(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={msg.type} onClose={() => setMsg(p => ({ ...p, open: false }))} sx={{ width: '100%' }}>
          {msg.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
